import { useLayoutEffect, useState, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Divider, List, Typography, useMediaQuery, TextField, InputAdornment } from '@mui/material';
import { SearchOutlined } from '@ant-design/icons';

// project import
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import menuItem from '@/from-theme/menu-items';

import useConfig from '@/from-theme/hooks/useConfig';
import { HORIZONTAL_MAX_ITEM } from 'config';
import { useGetMenuMaster } from 'api/menu';
import { MenuOrientation } from 'config';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = () => {
  const theme = useTheme();
  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const [selectedItems, setSelectedItems] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [menuItems, setMenuItems] = useState({ items: [] });
  const [query, setQuery] = useState('');

  useLayoutEffect(() => {
    setMenuItems(menuItem);
    // eslint-disable-next-line
  }, [menuItem]);

  const filterItems = (items, q) => {
    if (!q) return items;
    const tokens = q
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    const safeString = (value) => {
      if (value === undefined || value === null) return '';
      if (typeof value === 'string') return value.toLowerCase();
      return String(value).toLowerCase();
    };

    const itemMatchesTokens = (it) => {
      const hay = [it.title, it.caption, it.url].map(safeString).join(' ');
      return tokens.every((t) => hay.includes(t));
    };

    const filterRecursive = (arr) => {
      const out = [];
      arr.forEach((it) => {
        const children = it.children ? filterRecursive(it.children) : [];
        if (itemMatchesTokens(it) || (children && children.length > 0)) {
          out.push({ ...it, ...(children.length ? { children } : {}) });
        }
      });
      return out;
    };

    return filterRecursive(items);
  };

  const visibleMenuItems = useMemo(() => {
    if (!query) return menuItems.items;
    return filterItems(menuItems.items, query);
  }, [menuItems.items, query]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = visibleMenuItems.length - 1;
  let remItems = [];
  let lastItemId;

  //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by
  //giving horizontal max
  // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array
  // and set into remItems

  if (lastItem && lastItem < visibleMenuItems.length) {
    lastItemId = visibleMenuItems[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = visibleMenuItems.slice(lastItem - 1, visibleMenuItems.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navGroups = visibleMenuItems.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={item.id} {...(isHorizontal && { sx: { mt: 0.5 } })}>
              {!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
              <NavItem item={item} level={1} isParents />
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            lastItem={lastItem}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
            searchActive={!!query}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        ...(!isHorizontal && {
          '& > ul:first-of-type': { mt: 0 }
        }),
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {drawerOpen && (
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search menu"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              )
            }}
          />
        </Box>
      )}
      {navGroups}
    </Box>
  );
};

export default Navigation;
